// EN mirror of /citations — authoritative sources aggregation
import { createFileRoute, Link } from "@tanstack/react-router";
import { ExternalLink, ShieldCheck } from "lucide-react";
import { QUICK_ANSWERS } from "@/data/quick-answers";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { BRAND_URL } from "@/lib/brand";

const TITLE = "Citations & Authoritative Sources | NYC Language Institute";
const DESC = "Every answer on this site is anchored to a real.gov.th, embassy, Hague Conference. Get your timeline and checklist in minutes — chat now.";

type Row = { name: string; url: string; usedBy: { id: string; q: string }[] };
function buildSources(): Row[] {
  const map = new Map<string, Row>();
  for (const qa of QUICK_ANSWERS) {
    for (const s of qa.sources ?? []) {
      const row = map.get(s.url) ?? { name: s.name, url: s.url, usedBy: [] };
      row.usedBy.push({ id: qa.id, q: qa.question });
      map.set(s.url, row);
    }
  }
  return [...map.values()].sort((a, b) => b.usedBy.length - a.usedBy.length);
}

export const Route = createFileRoute("/en/citations")({
  head: () => ({
    meta: [
      { title: ctaTitle(TITLE, "/en/citations", "en")}, { name: "description", content: ctaDesc(DESC, "/en/citations", "en")},
      { property: "og:title", content: ctaTitle(TITLE, "/en/citations", "en")}, { property: "og:description", content: ctaDesc(DESC, "/en/citations", "en")},
      { property: "og:url", content: `${BRAND_URL}/en/citations` }, { property: "og:locale", content: "en_US" },
    ],
    links: [
      { rel: "canonical", href: `${BRAND_URL}/en/citations` },
      ...hreflangLinks("/citations", "/en/citations"),
    ],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org", "@type": "CollectionPage",
        name: TITLE, url: `${BRAND_URL}/en/citations`, inLanguage: "en",
        mainEntity: {
          "@type": "ItemList",
          itemListElement: buildSources().slice(0, 50).map((s, i) => ({
            "@type": "ListItem", position: i + 1,
            item: { "@type": "CreativeWork", name: s.name, url: s.url },
          })),
        },
      }),
    }],
  }),
  component: EnCitations,
});

function EnCitations() {
  const rows = buildSources();
  return (
    <main className="min-h-screen bg-white">
      <section className="bg-navy-deep px-4 py-14 text-white">
        <div className="mx-auto max-w-5xl">
          <Link to="/en" className="text-xs text-amber-300 hover:underline">← EN home</Link>
          <div className="mt-3 flex items-center gap-3">
            <ShieldCheck className="h-8 w-8 text-amber-300" aria-hidden />
            <h1 className="text-3xl font-extrabold sm:text-4xl">Citations & authoritative sources</h1>
          </div>
          <p className="mt-3 max-w-2xl text-white/80">
            {rows.length} distinct authoritative URLs power {QUICK_ANSWERS.length} quick-answer entries across this site.
            Every claim we publish about visas, apostille, notarial acts, or translation is verified against a primary source below.
          </p>
        </div>
      </section>
      <section className="px-4 py-12">
        <div className="mx-auto max-w-5xl space-y-3">
          {rows.map((r) => (
            <article key={r.url} className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="flex flex-wrap items-baseline justify-between gap-3">
                <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-base font-bold text-amber-700 underline">
                  {r.name} <ExternalLink className="ml-1 inline h-3.5 w-3.5" />
                </a>
                <span className="text-[11px] text-slate-500">cited by {r.usedBy.length}</span>
              </div>
              <p className="mt-1 break-all text-[11px] text-slate-500">{r.url}</p>
              <ul className="mt-2 flex flex-wrap gap-2">
                {r.usedBy.slice(0, 6).map((u) => (
                  <li key={u.id}><Link to="/en/ask" className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-700 hover:bg-amber-100">{u.q}</Link></li>
                ))}
              </ul>
            </article>
          ))}
          <p className="text-xs text-slate-500">Thai version: <Link to="/citations" className="text-amber-700 underline">/citations</Link></p>
        </div>
      </section>
    </main>
  );
}
